import { useEffect, useRef, useState } from "react";

import { useChatStore } from "@/stores/chatStore";

/**
 * Live extended-thinking panel. Fills the otherwise-blank reasoning gap before
 * the answer streams: shows the model's thoughts as they arrive, then collapses
 * to a "Thought for Ns" row once the answer (or a tool call) begins. Cosmetic —
 * the transcript is transient and clears with the turn.
 */
export function ThinkingPanel() {
  const thinkingText = useChatStore((s) => s.thinkingText);
  const expanded = useChatStore((s) => s.thinkingExpanded);
  const done = useChatStore((s) => s.thinkingDone);
  const startedAt = useChatStore((s) => s.thinkingStartedAt);
  const toggle = useChatStore((s) => s.toggleThinking);

  const [nowTick, setNowTick] = useState(() => Date.now());
  const [frozen, setFrozen] = useState<number | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Tick the live timer while thinking; stop once done.
  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [done]);

  // New thinking burst → drop any frozen duration.
  useEffect(() => { setFrozen(null); }, [startedAt]);

  // Freeze the duration when the step finishes so the label stops ticking.
  useEffect(() => {
    if (done && startedAt != null && frozen == null) setFrozen(Math.max(0, Date.now() - startedAt));
  }, [done, startedAt, frozen]);

  // Keep the latest reasoning in view while it streams.
  useEffect(() => {
    if (expanded && bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [thinkingText, expanded]);

  const ms = done ? (frozen ?? 0) : startedAt != null ? nowTick - startedAt : 0;
  const sec = Math.max(0, Math.round(ms / 1000));

  return (
    <div className="rounded-lg border border-space-20 bg-space-10/60 overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-space-50 hover:text-space transition-colors"
      >
        <svg
          className={`w-3 h-3 shrink-0 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <span>{done ? `Thought for ${sec}s` : "Thinking…"}</span>
        {!done && (
          <span className="flex gap-0.5 ml-0.5">
            <span className="w-1 h-1 rounded-full bg-jove-60 animate-pulse [animation-delay:-0.3s]" />
            <span className="w-1 h-1 rounded-full bg-jove-60 animate-pulse [animation-delay:-0.15s]" />
            <span className="w-1 h-1 rounded-full bg-jove-60 animate-pulse" />
          </span>
        )}
      </button>
      {expanded && (
        <div
          ref={bodyRef}
          className="px-3 pb-2.5 max-h-40 overflow-y-auto text-xs leading-relaxed text-space-40 whitespace-pre-wrap break-words border-t border-space-20/70"
        >
          {thinkingText}
        </div>
      )}
    </div>
  );
}

/**
 * Persisted counterpart to {@link ThinkingPanel}: the "Thought for Ns"
 * disclosure anchored above an answer in the transcript. Static (no live timer
 * or store wiring) and collapsed by default — click to reveal the transcript.
 * Rendered by MessageBubble for ``thinking`` rows, so it survives reload.
 */
export function MessageThinkingPanel({
  text,
  durationMs,
}: {
  text: string;
  durationMs?: number | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const sec = durationMs != null ? Math.max(0, Math.round(durationMs / 1000)) : null;
  const label = sec != null ? `Thought for ${sec}s` : "Thoughts";

  return (
    <div className="rounded-lg border border-space-20 bg-space-10/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-space-50 hover:text-space transition-colors"
      >
        <svg
          className={`w-3 h-3 shrink-0 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <span>{label}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-2.5 max-h-80 overflow-y-auto text-xs leading-relaxed text-space-40 whitespace-pre-wrap break-words border-t border-space-20/70">
          {text}
        </div>
      )}
    </div>
  );
}
